import React, { useState } from 'react';
import { Checkbox, Grid, Typography, Paper, FormControlLabel } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { Days } from '../../../types/Organization';
import { WorkingHours } from '../../../types';
import { useToastContext } from '../Toast/context/ToastContext';

interface DayConfig {
	day: Days;
	enabled: boolean;
	startTime: Dayjs | null;
	endTime: Dayjs | null;
}

interface DayErrors {
	startTime?: string;
	endTime?: string;
}

export interface WorkingHoursPickerProps {
	value?: WorkingHours[];
	onChange?: (value: DayConfig[]) => void;
	days?: Days[];
	validateOnChange?: boolean;
	ref?: React.Ref<{
		validateAllDays: () => boolean;
		clearErrors: () => void;
	}>;
}

const WorkingHoursPicker: React.FC<WorkingHoursPickerProps> = (props) => {
	const { value, onChange, days: daysProp = [] } = props;

	// Create initial state based on provided days
	const createInitialState = (days: Days[]): DayConfig[] => {
		return days.map((day) => ({
			day,
			enabled: false,
			startTime: null,
			endTime: null,
		}));
	};

	const [days, setDays] = useState<DayConfig[]>([]);
	const [dayErrors, setDayErrors] = useState<{[key: string]: DayErrors}>({});
	const { showToast } = useToastContext();
	// console.log("here value", value, days);

	// Initialize days when daysProp changes
	React.useEffect(() => {
		// console.log('re rendering');
		if (daysProp.length > 0) {
			setDays(createInitialState(daysProp));
		}
	}, [daysProp]);

	React.useEffect(() => {
		if (value && Array.isArray(value) && daysProp.length > 0) {
			const mappedDays = daysProp.map((propDay) => {
				const found = value.find((v) => v.day === propDay['@id']);
				// console.log('found', found, propDay);
				return {
					'@id': found ? found['@id'] : '',
					day: propDay,
					enabled: found ? !found.closed : false,
					startTime: found && found.openAt ? dayjs(found.openAt, 'HH:mm:ss') : null,
					endTime: found && found.closeAt ? dayjs(found.closeAt, 'HH:mm:ss') : null,
				};
			});
			// console.log('updating mappedDays', mappedDays);
			setDays(mappedDays);
		}
	}, [value, daysProp]);

	const handleCheckboxChange = (index: number) => {
		const updated = [...days];
		updated[index].enabled = !updated[index].enabled;

		if (!updated[index].enabled) {
			// If disabling, clear the times and errors
			updated[index].startTime = null;
			updated[index].endTime = null;
			
			// Clear errors for this day
			const dayId = updated[index].day['@id'];
			setDayErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors[dayId];
				return newErrors;
			});
		} else {
			// If enabling, try to find the last filled working hours
			const lastFilledDay = updated
				.slice(0, index) // Look at previous days only
				.reverse() // Start from the most recent
				.find((day) => day.enabled && day.startTime && day.endTime);

			if (lastFilledDay) {
				// Copy the times from the last filled day
				updated[index].startTime = dayjs(lastFilledDay.startTime);
				updated[index].endTime = dayjs(lastFilledDay.endTime);
			} else {
				// Show helpful message when enabling without default times
				showToast('success', `Please set start and end times for ${updated[index].day.label}`);
			}
		}
		setDays(updated);
		onChange?.(updated);
	};

	// Validation function for individual day
	const validateDayTimes = (dayConfig: DayConfig): DayErrors => {
		const errors: DayErrors = {};
		
		if (dayConfig.enabled) {
			if (!dayConfig.startTime) {
				errors.startTime = 'Start time is required for enabled days';
			}
			if (!dayConfig.endTime) {
				errors.endTime = 'End time is required for enabled days';
			}
		}
		
		return errors;
	};

	// Validate all days and show toast for general errors
	const validateAllDays = (daysData: DayConfig[]) => {
		const newErrors: {[key: string]: DayErrors} = {};
		let hasErrors = false;
		let incompleteCount = 0;

		daysData.forEach((day) => {
			const dayId = day.day['@id'];
			const dayErrors = validateDayTimes(day);
			if (Object.keys(dayErrors).length > 0) {
				newErrors[dayId] = dayErrors;
				hasErrors = true;
				if (day.enabled && (!day.startTime || !day.endTime)) {
					incompleteCount++;
				}
			}
		});

		setDayErrors(newErrors);

		// Show toast message for incomplete working hours
		if (incompleteCount > 0) {
			const message = incompleteCount === 1 
				? 'Please complete the working hours for the selected day'
				: `Please complete the working hours for ${incompleteCount} selected days`;
			showToast('error', message);
		}

		return !hasErrors;
	};

	const handleTimeChange = (index: number, time: Dayjs | null, isStart: boolean) => {
		const updated = [...days];
		if (isStart) updated[index].startTime = time;
		else updated[index].endTime = time;

		// Clear errors for this day when user makes changes
		const dayId = updated[index].day['@id'];
		setDayErrors(prev => {
			const newErrors = { ...prev };
			if (newErrors[dayId]) {
				if (isStart) delete newErrors[dayId].startTime;
				else delete newErrors[dayId].endTime;
				
				// Remove the day errors if no errors left
				if (Object.keys(newErrors[dayId]).length === 0) {
					delete newErrors[dayId];
				}
			}
			return newErrors;
		});

		setDays(updated);
		
		// Validate if validateOnChange prop is true
		if (props.validateOnChange) {
			validateAllDays(updated);
		}
		
		onChange?.(updated);
	};



	// Expose validation function via ref for parent components
	React.useImperativeHandle(props.ref, () => ({
		validateAllDays: () => validateAllDays(days),
		clearErrors: () => setDayErrors({})
	}));

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
		<Paper elevation={0} sx={{ py: 4, width: '100%', maxWidth: 800 }}>
				<Typography
					variant="h2"
					style={{
						color: '#333',
						marginBottom: '15px',
						fontSize: '1.3rem',
					}}
					gutterBottom
				>
					Business Hours
				</Typography>

				{days.map((d, index) => (
					<Grid
						container
						spacing={2}
						alignItems="center"
						key={d.day['@id']}
						sx={{
							mb: 1,
							borderRadius: 1,
							p: 1,
						}}
					>
						<Grid item xs={4}>
							<FormControlLabel
								control={
									<Checkbox checked={d.enabled} onChange={() => handleCheckboxChange(index)} />
								}
								label={d.day.label}
							/>
						</Grid>

				<Grid item xs={4}>
					<TimePicker
						label="Start Time"
						value={d.startTime}
						onChange={(newTime) => handleTimeChange(index, newTime, true)}
						disabled={!d.enabled}
						slotProps={{ 
							textField: { 
								fullWidth: true,
								error: !!(dayErrors[d.day['@id']]?.startTime),
								helperText: dayErrors[d.day['@id']]?.startTime || ''
							} 
						}}
					/>
				</Grid>

				<Grid item xs={4}>
					<TimePicker
						label="End Time"
						value={d.endTime}
						onChange={(newTime) => handleTimeChange(index, newTime, false)}
						disabled={!d.enabled}
						slotProps={{ 
							textField: { 
								fullWidth: true,
								error: !!(dayErrors[d.day['@id']]?.endTime),
								helperText: dayErrors[d.day['@id']]?.endTime || ''
							} 
						}}
					/>
				</Grid>						{/* Removed save button - changes are saved with form submit */}
					</Grid>
				))}

				{/* <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
					** Note: Set your weekly working hours. These hours will be used for scheduling and availability.
				</Typography> */}

				{/* <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
					<Button variant="outlined">Cancel</Button>
					<Button variant="contained" onClick={handleSave}>
						Save
					</Button>
				</Box> */}
			</Paper>
		</LocalizationProvider>
	);
};

export default React.memo(WorkingHoursPicker);
